import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronDown, Clock, CreditCard, Package, Star, Stethoscope } from "lucide-react";
import { formatINR } from "../../lib/constants";
import { downloadGuide } from "../EbookReader";

const PAID = ["paid", "confirmed", "processing", "packed", "shipped", "delivered", "completed"];
export const isPaid = (o) => PAID.includes(String(o.payment_status || "").toLowerCase());

const statusTone = (status) => ({
    delivered: "text-lime border-lime/40", dispatched: "text-gold border-gold/40",
    cancelled: "text-red-400 border-red-400/40", returned: "text-red-400 border-red-400/40",
}[status] || "text-zinc-300 border-white/15");

/** Shell that applies the colours staff picked in the CRM. */
export const TileShell = ({ tile, icon: Icon, children, testId }) => (
    <section data-testid={testId || `tile-${tile.type}`}
        className="bg-bg1 border border-white/10 p-6"
        style={tile.bg ? { backgroundColor: tile.bg } : undefined}>
        {tile.heading && (
            <h2 className="font-display text-xl tracking-tight flex items-center gap-2"
                style={tile.heading_color ? { color: tile.heading_color } : undefined}>
                {Icon && <Icon className="w-4 h-4 text-gold" />} {tile.heading}
            </h2>
        )}
        {tile.body && (
            <p className="text-xs text-zinc-500 mt-1" style={tile.body_color ? { color: tile.body_color } : undefined}>
                {tile.body}
            </p>
        )}
        {children}
    </section>
);

export const TileButton = ({ button, testId }) => {
    if (!button?.label) return null;
    const style = {
        ...(button.bg ? { backgroundColor: button.bg, borderColor: button.bg } : {}),
        ...(button.color ? { color: button.color } : {}),
    };
    return (
        <Link to={button.href || "#"} data-testid={testId} className="btn-pill btn-primary mt-4 inline-flex"
            style={Object.keys(style).length ? style : undefined}>
            {button.label}
        </Link>
    );
};


// ---------------------------------------------------------------- orders
const EbookRow = ({ guide, paid, onRead }) => (
    <div className="flex gap-4 border border-white/8 p-3" data-testid={`order-ebook-${guide.slug}`}>
        {guide.images?.[0] ? (
            <img src={guide.images[0]} alt={guide.name} className="w-20 h-28 object-cover shrink-0" />
        ) : (
            <div className="w-20 h-28 bg-bg0 border border-white/10 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-zinc-600" />
            </div>
        )}
        <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{guide.name}</div>
            {!paid ? (
                <div className="mono text-[10px] tracking-[0.14em] text-zinc-500 mt-2">
                    UNLOCKS ONCE PAYMENT IS CONFIRMED
                </div>
            ) : !guide.has_digital_file ? (
                <div className="mono text-[10px] tracking-[0.14em] text-zinc-500 mt-2">
                    FILE IS BEING ADDED — WE&apos;LL EMAIL YOU
                </div>
            ) : (
                <div className="flex flex-wrap items-center gap-3 mt-3">
                    <button onClick={() => onRead({ slug: guide.slug, name: guide.name, fileName: guide.digital_file_name })}
                        data-testid={`order-read-${guide.slug}`} className="btn-pill btn-primary">READ</button>
                    <button onClick={() => downloadGuide(guide.slug, guide.digital_file_name)}
                        data-testid={`order-download-${guide.slug}`}
                        className="mono text-[10px] tracking-[0.16em] text-zinc-400 hover:text-white">DOWNLOAD</button>
                </div>
            )}
        </div>
    </div>
);

const orderGuides = (order, products) => (order.items || [])
    .map((i) => products.find((p) => p.shop_type === "ebook"
        && (p.id === i.product_id || p.slug === (i.slug || i.product_slug))))
    .filter(Boolean);

const OrderRow = ({ order, latest, products, onRead }) => {
    const [open, setOpen] = useState(latest);
    const addr = order.address || {};
    const guides = orderGuides(order, products);
    return (
        <div className="border border-white/8" data-testid={`account-order-${order.order_number}`}>
            <button onClick={() => setOpen((v) => !v)} data-testid={`order-toggle-${order.order_number}`}
                className="w-full text-left p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="mono text-xs text-gold flex items-center gap-2">
                        {order.order_number}
                        {latest && <span className="mono text-[9px] text-lime border border-lime/40 px-1.5">LATEST</span>}
                    </div>
                    <div className="text-sm text-zinc-400 mt-1">
                        {order.items?.map((i) => `${i.product_name} × ${i.quantity}`).join(", ")}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="font-bold">{formatINR(order.total)}</div>
                        <span className={`inline-block mt-1 mono text-[9px] tracking-[0.14em] border px-2 py-0.5 ${statusTone(order.status)}`}>
                            {String(order.status).toUpperCase()}
                        </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>
            {open && (
                <div className="border-t border-white/8 px-4 py-4 space-y-3">
                    <div className="text-xs text-zinc-500">
                        Placed {String(order.created_at || "").slice(0, 10)} · Delivering to{" "}
                        {[addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
                    </div>
                    {guides.map((g) => (
                        <EbookRow key={g.slug} guide={g} paid={isPaid(order)} onRead={onRead} />
                    ))}
                    <Link to={`/order/${order.order_number}`} data-testid={`order-details-${order.order_number}`}
                        className="mono text-[10px] tracking-[0.16em] text-gold hover:underline inline-block">
                        FULL ORDER DETAILS →
                    </Link>
                </div>
            )}
        </div>
    );
};

const Purchases = ({ tile, ctx }) => (
    <TileShell tile={tile} icon={Package} testId="account-orders">
        {ctx.purchases.length === 0 ? (
            <div className="py-10 text-center text-zinc-500 text-sm">
                Nothing purchased yet. <Link to="/shop" className="text-gold">Start shopping →</Link>
            </div>
        ) : (
            <div className="mt-5 space-y-3">
                {ctx.purchases.map((o, i) => (
                    <OrderRow key={o.order_number} order={o} latest={i === 0}
                        products={ctx.products} onRead={ctx.onRead} />
                ))}
            </div>
        )}
        <TileButton button={tile.button} testId="tile-purchases-button" />
    </TileShell>
);

const PendingOrders = ({ tile, ctx }) => {
    const [open, setOpen] = useState(true);
    const [all, setAll] = useState(false);
    const rows = all ? ctx.unpaid : ctx.unpaid.slice(0, 2);
    if (!ctx.unpaid.length && !ctx.preview) return null;

    return (
        <TileShell tile={tile} icon={CreditCard} testId="account-pending-payments">
            <button onClick={() => setOpen((v) => !v)} data-testid="pending-orders-toggle"
                className="mt-3 mono text-[10px] tracking-[0.16em] text-gold inline-flex items-center gap-2">
                {open ? "HIDE" : `SHOW (${ctx.unpaid.length})`}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <>
                    <div className="mt-4 space-y-3">
                        {rows.map((o) => (
                            <div key={o.order_number} data-testid={`pending-order-${o.order_number}`}
                                className="border border-white/8 p-4 flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <div className="mono text-xs text-gold">{o.order_number}</div>
                                    <div className="text-sm text-zinc-400 mt-1">
                                        {o.items?.map((i) => `${i.product_name} × ${i.quantity}`).join(", ")}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="font-bold">{formatINR(o.total)}</div>
                                    <button onClick={() => ctx.onPay(o.order_number)}
                                        disabled={ctx.paying === o.order_number}
                                        data-testid={`pending-pay-${o.order_number}`}
                                        className="btn-pill btn-primary disabled:opacity-50">
                                        {ctx.paying === o.order_number ? "OPENING…" : "PAY NOW"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {ctx.unpaid.length > 2 && (
                        <button onClick={() => setAll((v) => !v)} data-testid="pending-show-all"
                            className="mt-4 mono text-[10px] tracking-[0.16em] text-zinc-400 hover:text-white">
                            {all ? "SHOW LESS" : `SHOW ALL (${ctx.unpaid.length})`}
                        </button>
                    )}
                </>
            )}
        </TileShell>
    );
};


// ---------------------------------------------------------------- library & content
const Guides = ({ tile, ctx }) => {
    if (!ctx.guides.length && !ctx.preview) return null;
    return (
        <TileShell tile={tile} icon={BookOpen} testId="account-library">
            <div className="mt-4 space-y-3">
                {!ctx.guides.length && ctx.preview && (
                    <p className="text-sm text-zinc-500 mt-4">Guides a customer has bought appear here.</p>
                )}
                {ctx.guides.map((g) => (
                    <div key={g.slug} className="border border-white/8 p-3 flex items-center gap-3"
                        data-testid={`library-item-${g.slug}`}>
                        {g.images?.[0] && <img src={g.images[0]} alt={g.name} className="w-12 h-16 object-cover shrink-0" />}
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">{g.name}</div>
                            <div className="mono text-[9px] tracking-[0.14em] text-zinc-500 mt-1">
                                {g.free ? "FREE GUIDE" : g.order_number || "PURCHASED"}
                            </div>
                        </div>
                        {g.file_pending ? (
                            <span className="mono text-[9px] tracking-[0.14em] text-zinc-500 text-right shrink-0">
                                FILE BEING<br />ADDED
                            </span>
                        ) : g.unlocked ? (
                            <div className="flex flex-col items-end gap-1 shrink-0">
                                <button onClick={() => ctx.onRead({ slug: g.slug, name: g.name, fileName: g.file_name })}
                                    data-testid={`library-read-${g.slug}`}
                                    className="mono text-[10px] tracking-[0.16em] text-gold hover:underline">READ</button>
                                <button onClick={() => downloadGuide(g.slug, g.file_name)}
                                    data-testid={`library-download-${g.slug}`}
                                    className="mono text-[9px] tracking-[0.14em] text-zinc-500 hover:text-white">DOWNLOAD</button>
                            </div>
                        ) : (
                            <span className="mono text-[9px] tracking-[0.14em] text-zinc-500 shrink-0">AWAITING PAYMENT</span>
                        )}
                    </div>
                ))}
            </div>
        </TileShell>
    );
};

const ProductGrid = ({ tile, ctx, onlyEbooks, testId }) => {
    const pool = ctx.products.filter((p) => (onlyEbooks ? p.shop_type === "ebook" : p.shop_type !== "ebook"));
    const picks = (tile.product_ids?.length
        ? ctx.products.filter((p) => tile.product_ids.includes(p.id))
        : pool).slice(0, tile.limit || 3);
    if (!picks.length) return null;
    return (
        <TileShell tile={tile} testId={testId}>
            <div className="grid sm:grid-cols-3 gap-4 mt-4">
                {picks.map((p) => (
                    <Link key={p.slug} to={`/product/${p.slug}`} data-testid={`tile-product-${p.slug}`}
                        className="border border-white/8 p-3 hover:border-gold/40 transition-colors">
                        {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-24 object-cover mb-2" />}
                        <div className="text-sm font-semibold">{p.name}</div>
                        <div className="mono text-[10px] tracking-[0.14em] text-gold mt-1">{formatINR(p.price)}</div>
                    </Link>
                ))}
            </div>
            <TileButton button={tile.button} testId={`${testId}-button`} />
        </TileShell>
    );
};

const BlogTile = ({ tile, ctx }) => {
    const posts = (ctx.posts || []).slice(0, tile.limit || 3);
    if (!posts.length) return null;
    return (
        <TileShell tile={tile} testId="tile-blog">
            <ul className="mt-4 space-y-3">
                {posts.map((p) => (
                    <li key={p.slug}>
                        <Link to={`/blog/${p.slug}`} className="text-sm hover:text-gold transition-colors"
                            data-testid={`tile-post-${p.slug}`}>{p.title}</Link>
                    </li>
                ))}
            </ul>
            <TileButton button={tile.button} testId="tile-blog-button" />
        </TileShell>
    );
};

const ConsultTile = ({ tile }) => (
    <TileShell tile={tile} icon={Stethoscope} testId="tile-consultations">
        <TileButton button={tile.button} testId="tile-consultations-button" />
        {!tile.button?.label && (
            <Link to="/account/consultations" className="btn-pill btn-secondary mt-4 inline-flex"
                data-testid="tile-consultations-link">MY CONSULTATIONS</Link>
        )}
    </TileShell>
);

const FreeConsult = ({ tile, ctx }) => (
    <section data-testid="account-free-consult"
        className="bg-bg1 border border-gold/30 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
        style={tile.bg ? { backgroundColor: tile.bg } : undefined}>
        <div>
            <div className="font-display text-xl tracking-tight"
                style={tile.heading_color ? { color: tile.heading_color } : undefined}>
                {ctx.freeConsult ? (tile.heading || "Your free consultation") : "Book another consultation"}
            </div>
            <p className="text-sm text-zinc-400 mt-1"
                style={tile.body_color ? { color: tile.body_color } : undefined}>
                {ctx.freeConsult
                    ? (tile.body || "One consultation with our practitioner is on us. Pick a slot that suits you.")
                    : "You've used your free session. Further consultations are booked at the usual package price."}
            </p>
        </div>
        <Link to={tile.button?.href || "/consultation/book"} data-testid="account-book-consult"
            className="btn-pill btn-primary shrink-0"
            style={tile.button?.bg ? { backgroundColor: tile.button.bg, borderColor: tile.button.bg,
                color: tile.button.color || undefined } : undefined}>
            {ctx.freeConsult ? (tile.button?.label || "BOOK FREE CONSULTATION") : "BOOK A CONSULTATION"}
        </Link>
    </section>
);

const Profile = ({ tile, ctx }) => (
    <TileShell tile={tile} testId="account-profile">
        <div className="mt-4 space-y-3">
            <input className="w-full bg-bg0 border border-white/10 px-4 py-3 text-sm outline-none focus:border-gold"
                value={ctx.profile.name} onChange={(e) => ctx.setProfile({ ...ctx.profile, name: e.target.value })}
                placeholder="Full name" data-testid="profile-name" />
            <input className="w-full bg-bg0 border border-white/10 px-4 py-3 text-sm outline-none focus:border-gold"
                value={ctx.profile.phone} onChange={(e) => ctx.setProfile({ ...ctx.profile, phone: e.target.value })}
                placeholder="Phone" data-testid="profile-phone" />
            <button onClick={ctx.saveProfile} className="btn-pill btn-primary w-full" data-testid="profile-save">
                SAVE DETAILS
            </button>
        </div>
    </TileShell>
);

const FirstOrderOffer = ({ tile, ctx }) => {
    if (ctx.customer?.first_order_done && !ctx.preview) return null;
    return (
        <section data-testid="account-first-order-offer" className="bg-bg1 border border-gold/30 p-6"
            style={tile.bg ? { backgroundColor: tile.bg } : undefined}>
            <div className="mono text-[10px] tracking-[0.18em] text-zinc-500">
                {(tile.heading || "First order offer").toUpperCase()}
            </div>
            <div className="font-display text-3xl mt-2 text-gold"
                style={tile.heading_color ? { color: tile.heading_color } : undefined}>50% OFF</div>
            <div className="mono text-[10px] text-gold mt-2">CODE RHMFIRST</div>
            {tile.body && <p className="text-xs text-zinc-500 mt-2">{tile.body}</p>}
        </section>
    );
};

const Reorders = ({ tile, ctx }) => (
    <TileShell tile={tile} icon={Clock} testId="account-reorders">
        {ctx.reorders.length === 0 ? (
            <p className="text-sm text-zinc-500 mt-4">{tile.body ? "" : "Nothing due yet."}</p>
        ) : (
            <ul className="mt-4 space-y-3">
                {ctx.reorders.map((r) => (
                    <li key={r.id} className="border border-white/8 p-3">
                        <div className="text-sm font-semibold">{r.product_name}</div>
                        <div className="mono text-[10px] tracking-[0.14em] text-zinc-500 mt-1">
                            {String(r.state).toUpperCase()} · {r.expected_date}
                        </div>
                    </li>
                ))}
            </ul>
        )}
    </TileShell>
);

const ImageTile = ({ tile }) => {
    if (!tile.image_url) return null;
    const img = <img src={tile.image_url} alt={tile.heading || ""} className="w-full object-cover" />;
    return (
        <TileShell tile={tile} testId="tile-image">
            <div className="mt-4">{tile.link ? <Link to={tile.link} data-testid="tile-image-link">{img}</Link> : img}</div>
            <TileButton button={tile.button} testId="tile-image-button" />
        </TileShell>
    );
};

const ReviewTile = ({ tile, ctx }) => (
    <TileShell tile={tile} icon={Star} testId="tile-review">
        {ctx.purchases.length > 0 && !tile.button?.label && (
            <Link to={`/product/${ctx.purchases[0].items?.[0]?.slug || ""}`} data-testid="tile-review-link"
                className="btn-pill btn-secondary mt-4 inline-flex">WRITE A REVIEW</Link>
        )}
        <TileButton button={tile.button} testId="tile-review-button" />
    </TileShell>
);

const TextTile = ({ tile }) => (
    <TileShell tile={tile} testId="tile-text">
        <TileButton button={tile.button} testId="tile-text-button" />
    </TileShell>
);

const ButtonTile = ({ tile }) => {
    if (!tile.button?.label) return null;
    return (
        <div data-testid="tile-button">
            <TileButton button={tile.button} testId="tile-button-link" />
        </div>
    );
};

export const TILE_RENDERERS = {
    orders_purchases: Purchases,
    orders_pending: PendingOrders,
    guides: Guides,
    products: (p) => <ProductGrid {...p} testId="tile-products" />,
    books: (p) => <ProductGrid {...p} onlyEbooks testId="tile-books" />,
    blog: BlogTile,
    consultations: ConsultTile,
    free_consult: FreeConsult,
    profile: Profile,
    first_order_offer: FirstOrderOffer,
    reorders: Reorders,
    image: ImageTile,
    review: ReviewTile,
    text: TextTile,
    button: ButtonTile,
};

export const TILE_LABELS = {
    orders_purchases: "My Purchases", orders_pending: "Pending Orders", guides: "My Guides",
    products: "Products", books: "Books / E-books", blog: "Blog", consultations: "Consultations",
    free_consult: "Free consultation", profile: "My Details", first_order_offer: "First order offer",
    reorders: "Reorder reminders", image: "Image", review: "Review", text: "Custom text",
    button: "Button",
};

export const renderTile = (tile, ctx) => {
    if (tile.visible === false) return null;
    const Renderer = TILE_RENDERERS[tile.type];
    if (!Renderer) return null;
    return <Renderer key={tile.id} tile={tile} ctx={ctx} />;
};
